import { useMemo, type ReactNode } from 'react';
import { motion } from 'framer-motion';
import { Star, Radio } from 'lucide-react';
import type { ScheduleEntry } from '../../lib/scheduleParsing';

export type AccentTone = 'blue' | 'violet' | 'emerald' | 'amber' | 'cyan' | 'pink';

interface ScheduleCalendarProps<T> {
  entries: ScheduleEntry<T>[];
  activeId: string | null;
  hoveredId: string | null;
  onItemClick: (id: string) => void;
  onItemHover: (id: string | null) => void;
  renderPrimary: (data: T) => ReactNode;
  renderSecondary?: (data: T) => ReactNode;
  accentTone?: (data: T) => AccentTone;
  /** When true, Thursday of Week 2 gets the glowing 'Live' treatment */
  showWebinarAnchor?: boolean;
  title: string;
  subtitle?: string;
  /** Optional count chip shown in the header, e.g. "8 emails" */
  countLabel?: string;
  /** Compact variant — smaller cells & header, for per-tab contextual calendars */
  dense?: boolean;
}

// Default state — slightly translucent colored fill
const ACCENT_BG_SOFT: Record<AccentTone, string> = {
  blue:    'bg-blue-500/85',
  violet:  'bg-violet-500/85',
  emerald: 'bg-emerald-500/85',
  amber:   'bg-amber-500/85',
  cyan:    'bg-cyan-500/85',
  pink:    'bg-pink-400/80',
};

// Active/hover — full fill
const ACCENT_BG_FULL: Record<AccentTone, string> = {
  blue:    'bg-blue-500',
  violet:  'bg-violet-500',
  emerald: 'bg-emerald-500',
  amber:   'bg-amber-500',
  cyan:    'bg-cyan-500',
  pink:    'bg-pink-400',
};

const ACCENT_GLOW: Record<AccentTone, string> = {
  blue:    'shadow-[inset_0_0_20px_rgba(37,99,235,0.55),0_0_14px_rgba(37,99,235,0.55)]',
  violet:  'shadow-[inset_0_0_20px_rgba(139,92,246,0.55),0_0_14px_rgba(139,92,246,0.55)]',
  emerald: 'shadow-[inset_0_0_20px_rgba(16,185,129,0.55),0_0_14px_rgba(16,185,129,0.55)]',
  amber:   'shadow-[inset_0_0_20px_rgba(245,158,11,0.55),0_0_14px_rgba(245,158,11,0.55)]',
  cyan:    'shadow-[inset_0_0_20px_rgba(34,211,238,0.55),0_0_14px_rgba(34,211,238,0.55)]',
  pink:    'shadow-[inset_0_0_20px_rgba(244,143,177,0.6),0_0_14px_rgba(244,143,177,0.6)]',
};

// Pre-webinar content starts at day 1 (Mon W1). Webinar day lands on day 11 —
// that's mock Thursday of Week 2 since day 1 starts on Monday. dayOffset 0
// maps to day 11, dayOffset -10 maps to day 1, etc.
const WEBINAR_ANCHOR_DAY = 11;
const DAYS_IN_VIEW = 30;

interface DayBucket<T> {
  dayNumber: number;          // 1..30
  entries: ScheduleEntry<T>[]; // all items on this day
  isWebinar: boolean;
}

function buildDayBuckets<T>(
  entries: ScheduleEntry<T>[],
  showWebinarAnchor: boolean,
): DayBucket<T>[] {
  const buckets: DayBucket<T>[] = Array.from({ length: DAYS_IN_VIEW }, (_, i) => ({
    dayNumber: i + 1,
    entries: [],
    isWebinar: false,
  }));

  for (const entry of entries) {
    const off = entry.slot?.dayOffset;
    if (off == null) continue;
    const mockDay = WEBINAR_ANCHOR_DAY + off;
    if (mockDay < 1 || mockDay > DAYS_IN_VIEW) continue;
    buckets[mockDay - 1].entries.push(entry);
  }

  if (showWebinarAnchor) {
    buckets[WEBINAR_ANCHOR_DAY - 1].isWebinar = true;
  }

  return buckets;
}

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export function ScheduleCalendar<T>({
  entries,
  activeId,
  hoveredId,
  onItemClick,
  onItemHover,
  renderPrimary,
  renderSecondary,
  accentTone,
  showWebinarAnchor = true,
  title,
  subtitle,
  countLabel,
  dense = false,
}: ScheduleCalendarProps<T>) {
  const buckets = useMemo(
    () => buildDayBuckets(entries, showWebinarAnchor),
    [entries, showWebinarAnchor],
  );

  return (
    <div className="rounded-2xl overflow-hidden border border-slate-700/60 bg-slate-900/85 shadow-[0_12px_40px_rgba(2,6,23,0.55)]">
      {/* Header */}
      <div
        className={`flex items-center justify-between gap-3 border-b border-slate-800 ${
          dense ? 'px-4 pt-3 pb-3' : 'px-5 pt-5 pb-4'
        }`}
      >
        <div className="flex items-center gap-3 min-w-0">
          <div
            className={`flex items-center justify-center rounded-lg bg-slate-800/80 border border-slate-700 ${
              dense ? 'w-7 h-7' : 'w-9 h-9'
            }`}
          >
            <Radio className={`${dense ? 'w-3.5 h-3.5' : 'w-4 h-4'} text-blue-300`} />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.24em] text-slate-400 font-semibold">
              <span>Broadcast Schedule</span>
              {countLabel && (
                <span className="px-2 py-0.5 rounded-full bg-slate-800 border border-slate-700 text-slate-200 tracking-[0.18em]">
                  {countLabel}
                </span>
              )}
            </div>
            <div className={`${dense ? 'text-sm' : 'text-base'} font-semibold text-white truncate`}>
              {title}
            </div>
            {subtitle && (
              <div className="text-[12px] text-slate-400 truncate">{subtitle}</div>
            )}
          </div>
        </div>
      </div>

      {/* Body */}
      <div className={dense ? 'p-3' : 'p-5'}>
        {/* Weekday headers */}
        <div className={`grid grid-cols-7 ${dense ? 'gap-1 mb-1' : 'gap-2 mb-2'}`}>
          {DAY_LABELS.map((label) => (
            <div
              key={label}
              className={`text-[10px] uppercase tracking-[0.22em] text-slate-500 font-semibold text-center ${
                dense ? 'py-0.5' : 'py-1'
              }`}
            >
              {label}
            </div>
          ))}
        </div>

        {/* Month grid: 30 days in 5 rows × 7 cols */}
        <div className={`grid grid-cols-7 ${dense ? 'gap-1' : 'gap-2'}`}>
          {buckets.map((bucket) => (
            <DayCell
              key={bucket.dayNumber}
              bucket={bucket}
              activeId={activeId}
              hoveredId={hoveredId}
              onItemClick={onItemClick}
              onItemHover={onItemHover}
              renderPrimary={renderPrimary}
              renderSecondary={renderSecondary}
              accentTone={accentTone}
              dense={dense}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

interface DayCellProps<T> {
  bucket: DayBucket<T>;
  activeId: string | null;
  hoveredId: string | null;
  onItemClick: (id: string) => void;
  onItemHover: (id: string | null) => void;
  renderPrimary: (data: T) => ReactNode;
  renderSecondary?: (data: T) => ReactNode;
  accentTone?: (data: T) => AccentTone;
  dense?: boolean;
}

interface Segment<T> {
  key: string;
  isWebinar: boolean;
  tone?: AccentTone;
  entry?: ScheduleEntry<T>;
  flex: number;
}

function DayCell<T>({
  bucket,
  activeId,
  hoveredId,
  onItemClick,
  onItemHover,
  renderPrimary,
  renderSecondary,
  accentTone,
  dense = false,
}: DayCellProps<T>) {
  const { dayNumber, entries, isWebinar } = bucket;
  const hasItems = entries.length > 0;
  const cellHeight = dense ? 'h-14' : 'h-24';
  const dayNumberSize = dense ? 'text-[10px]' : 'text-[11px]';

  // Empty, non-webinar day — dim cell
  if (!isWebinar && !hasItems) {
    return (
      <div
        className={`${cellHeight} rounded-lg bg-slate-800/30 border border-slate-800/80 flex items-start justify-start ${
          dense ? 'p-1' : 'p-2'
        }`}
      >
        <span className={`${dayNumberSize} text-slate-600 tabular-nums`}>{dayNumber}</span>
      </div>
    );
  }

  // Build segments: webinar stripe first, then each entry stacked
  const segments: Segment<T>[] = [];
  if (isWebinar) {
    segments.push({ key: `d${dayNumber}-webinar`, isWebinar: true, flex: hasItems ? 1.3 : 1 });
  }
  entries.forEach((e) => {
    segments.push({
      key: e.id,
      isWebinar: false,
      tone: accentTone?.(e.data) ?? 'blue',
      entry: e,
      flex: 1,
    });
  });

  const multi = segments.length > 1;
  const ultraCompact = dense || segments.length >= 4;

  return (
    <div
      className={`relative ${cellHeight} rounded-lg overflow-hidden flex flex-col gap-px border ${
        isWebinar
          ? 'border-amber-300/50 shadow-[0_0_16px_rgba(245,158,11,0.35)]'
          : 'border-white/10 bg-slate-950/40'
      }`}
    >
      {/* Day number overlay */}
      <span
        className={`pointer-events-none absolute ${
          dense ? 'top-0.5 left-1' : 'top-1 left-1.5'
        } z-20 ${dayNumberSize} font-bold tabular-nums ${
          isWebinar
            ? 'text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)]'
            : 'text-white/95 drop-shadow-[0_1px_2px_rgba(0,0,0,0.75)]'
        }`}
      >
        {dayNumber}
      </span>

      {segments.map((seg) => {
        if (seg.isWebinar) {
          return (
            <WebinarSegment
              key={seg.key}
              flex={seg.flex}
              webinarOnly={!hasItems}
              otherCount={entries.length}
              ultraCompact={ultraCompact}
              dense={dense}
            />
          );
        }
        const entry = seg.entry!;
        const tone = seg.tone!;
        const isActive = entry.id === activeId;
        const isHovered = entry.id === hoveredId;
        const secondary = renderSecondary ? renderSecondary(entry.data) : null;
        const tooltip = [
          `Day ${dayNumber}`,
          String(renderPrimary(entry.data)),
          secondary ? String(secondary) : '',
        ]
          .filter(Boolean)
          .join(' · ');

        return (
          <motion.button
            key={seg.key}
            type="button"
            whileHover={{ scale: 1.01 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => onItemClick(entry.id)}
            onMouseEnter={() => onItemHover(entry.id)}
            onMouseLeave={() => onItemHover(null)}
            title={tooltip}
            className={`relative w-full transition-colors ${
              isActive || isHovered
                ? `${ACCENT_BG_FULL[tone]} ${ACCENT_GLOW[tone]}`
                : ACCENT_BG_SOFT[tone]
            }`}
            style={{ flexGrow: seg.flex, flexBasis: 0, minHeight: 0 }}
          >
            {/* Subtle top highlight to read as stacked events */}
            <span className="absolute inset-x-0 top-0 h-px bg-white/25 pointer-events-none" />
            {!dense && !multi && (
              <span className="absolute bottom-1 right-1.5 text-[9px] uppercase tracking-[0.16em] text-white/90 font-bold">
                #{entry.order + 1}
              </span>
            )}
            {!dense && multi && !ultraCompact && (
              <span className="absolute right-1.5 top-1/2 -translate-y-1/2 text-[9px] font-mono text-white/90">
                #{entry.order + 1}
              </span>
            )}
          </motion.button>
        );
      })}
    </div>
  );
}

interface WebinarSegmentProps {
  flex: number;
  webinarOnly: boolean;
  otherCount: number;
  ultraCompact: boolean;
  dense: boolean;
}

function WebinarSegment({ flex, webinarOnly, otherCount, ultraCompact, dense }: WebinarSegmentProps) {
  return (
    <div
      className="relative w-full flex items-center justify-center gap-1.5"
      style={{
        flexGrow: flex,
        flexBasis: 0,
        minHeight: 0,
        background:
          'linear-gradient(135deg, rgb(245,158,11), rgb(219,39,119) 60%, rgb(139,92,246))',
      }}
      title={
        webinarOnly
          ? 'Webinar — Live day'
          : `Webinar day · ${otherCount} item${otherCount === 1 ? '' : 's'}`
      }
    >
      {/* Top highlight to match other event stripes */}
      <span className="absolute inset-x-0 top-0 h-px bg-white/30 pointer-events-none" />

      {dense ? (
        <Star className="relative w-3 h-3 text-white drop-shadow" fill="currentColor" />
      ) : webinarOnly ? (
        <>
          <Star className="relative w-3.5 h-3.5 text-white drop-shadow" fill="currentColor" />
          <span className="relative text-[10px] uppercase tracking-[0.22em] text-white font-bold drop-shadow">
            Live
          </span>
        </>
      ) : (
        <>
          <Star
            className={`relative ${ultraCompact ? 'w-2.5 h-2.5' : 'w-3 h-3'} text-white drop-shadow`}
            fill="currentColor"
          />
          <span
            className={`relative ${ultraCompact ? 'text-[8px]' : 'text-[9px]'} uppercase tracking-[0.22em] text-white font-bold drop-shadow`}
          >
            Live · Webinar
          </span>
        </>
      )}
    </div>
  );
}
